import type { ClassConstructor, TargetMap } from 'class-transformer'
import { ResponseArray, ResponseFormater } from './response'
import {
  HTTPMethod,
  type ICustomArrayResponse,
  type IHTTP,
  type IHTTPCreateOptions,
} from './types'
import { cleanParams, getRequestHeaders, getUrl } from './utils'

/**
 * HTTP 请求类
 */
export default class HTTP<T> implements IHTTP<T> {
  private _options: IHTTPCreateOptions
  private _config?: RequestInit
  private _revalidate: number = 10
  private _response?: Promise<Response>
  private _ClassConstructor?: ClassConstructor<T>
  private _abortCtrl?: AbortController
  private _cacheMap: Map<string, Promise<Response>> = new Map()

  static instanceList: Set<HTTP<any>> = new Set()
  static _refreshCount = 0
  static _refreshRes?: Promise<Response>

  private constructor(
    _options: IHTTPCreateOptions,
    _ClassConstructor?: ClassConstructor<T>
  )
  private constructor(url: string, _ClassConstructor: ClassConstructor<T>)
  private constructor(
    options: IHTTPCreateOptions | string,
    _ClassConstructor?: ClassConstructor<T>
  )
  private constructor(
    options: IHTTPCreateOptions | string,
    _ClassConstructor?: ClassConstructor<T>
  ) {
    if (typeof options === 'string') {
      this._options = {
        url: options,
        cache: true,
      }
    } else {
      this._options = options
      if (this._options.cache === undefined) {
        this._options.cache = true
      }
    }
    this._ClassConstructor = _ClassConstructor
  }

  /**
   * 创建 HTTP 请求实例
   * @param options IHTTPCreateOptions | string
   * @returns
   */
  static create<T>(
    options: IHTTPCreateOptions | string,
    someClass?: ClassConstructor<T>
  ) {
    const instance = new HTTP<T>(options, someClass)
    HTTP.instanceList.add(instance)
    return instance
  }

  /**
   * 代理请求
   * @param method HTTPMethod
   * @returns
   */
  private _proxy(method?: HTTPMethod): HTTP<T> {
    const url = getUrl(this._options.url, cleanParams(this._options.params))

    this._abortCtrl = new AbortController()
    const init: RequestInit = {
      method: this._options.method || method,
      headers: {
        ...getRequestHeaders(url),
        ...this._options.headers,
      },
      body: this._options.data ? JSON.stringify(this._options.data) : undefined,
      signal: this._abortCtrl.signal,
      cache: this._options.cache ? 'default' : 'no-store',
      next: { revalidate: this._revalidate },
      ...this._config,
    }

    this._response = fetch(url, init)

    return this
  }

  /**
   * 获取请求结果
   */
  private async _result(): Promise<Response> {
    if (!this._response) {
      this.get()
    }

    const cacheKey = this._getCacheKey()
    if (this._options.cache && this._cacheMap.has(cacheKey)) {
      return this._cacheMap.get(cacheKey)!
    }

    const responsePromise = this._response!.then((response) => {
      if (!response.ok) {
        console.log('HTTP Error1:', response.status, response.statusText)
        throw response
      }
      return response
    }).catch((error) => {
      console.log('HTTP Error2:', error.status, error.statusText)
      if (error instanceof Response) {
        return ResponseFormater.formatError(error)
      }
      throw error
    })

    if (this._options.cache) {
      this._cacheMap.set(cacheKey, responsePromise)
    }

    return responsePromise
  }

  /**
   * 返回json格式数据
   */
  async json(targetMaps?: TargetMap[]): Promise<T> {
    const data = await this._result()
    return ResponseFormater.format<T>(data, this._ClassConstructor, targetMaps)
  }
  /**
   * 返回数组格式数据
   */
  async array(
    targetMaps?: TargetMap[],
    customArrayResponse?: ICustomArrayResponse<T>
  ): Promise<ResponseArray<T>> {
    const data = await this._result()
    return customArrayResponse
      ? customArrayResponse(data)
      : ResponseFormater.formatArray<T>(
          data,
          this._ClassConstructor,
          targetMaps
        )
  }

  /**
   * 重试请求
   */
  retry() {
    return this._proxy()
  }

  /**
   * 中断请求
   */
  abort() {
    if (this._abortCtrl) {
      this._abortCtrl.abort()
    }
    return this
  }

  /**
   * 设置options
   * @param paramOptions
   * @returns
   */
  with(paramOptions: Partial<IHTTPCreateOptions>) {
    this._options = { ...this._options, ...paramOptions }
    return this
  }

  /**
   * 设置请求参数params
   * @param params
   * @returns
   */
  withParams(params?: Record<string, any>) {
    if (!params) return this

    this._response = undefined
    this._options.params = params
    return this
  }
  /**
   * 设置请求参数data
   * @param data
   * @returns
   */
  withData(data?: Record<string, any>) {
    if (!data) return this

    this._response = undefined
    this._options.data = data
    return this
  }

  /**
   * 缓存key
   * @returns string
   */
  private _getCacheKey(): string {
    const method = this._options.method || HTTPMethod.GET
    const url = getUrl(this._options.url, this._options.params)
    const body = this._options.data ? JSON.stringify(this._options.data) : ''
    return `${method}:${url}:${body}`
  }

  /**
   * 清除缓存
   */
  clearCache() {
    this._cacheMap.clear()
  }

  /**
   * 设置 config
   * @param config RequestInit
   * @returns
   */
  config(config?: RequestInit) {
    if (!config) return this

    this._response = undefined
    this._config = config
    return this
  }

  /**
   * 设置 revalidate
   * @param revalidate number
   * @returns 
   */
  setRevalidate(revalidate: number) {
    this._revalidate = revalidate
    return this
  }

  get() {
    this._options.method = HTTPMethod.GET
    return this._proxy()
  }
  post() {
    this._options.method = HTTPMethod.POST
    return this._proxy()
  }
  patch() {
    this._options.method = HTTPMethod.PATCH
    return this._proxy()
  }
  put() {
    this._options.method = HTTPMethod.PUT
    return this._proxy()
  }
  delete() {
    this._options.method = HTTPMethod.DELETE
    return this._proxy()
  }

  static abortAll() {
    // 中断所有请求，并且从数组中移除
    HTTP.instanceList.forEach((http) => {
      http.abort()
      http.clearCache()
    })
    HTTP.instanceList.clear()
  }
}
